import { Link as MaterialLink } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { AuthData } from '../../../context/auth';
import {
	CancellationData,
	getReinstatementErrors,
	withCancellationContext,
} from '../../../context/CancellationProvider';
import { getDateErrors } from '../../../util/util';
import { StyledDetail, StyledSectionContainer } from '../../common';
import DefaultDatePicker from '../../form/DefaultDatePicker';

interface Props {
	authData: AuthData;
	cancellationData: CancellationData;
}

const useStyles = makeStyles(() => ({
	linkWrapper: {
		display: 'flex',
		justifyContent: 'flex-end',
	},
}));

const ReinstatementDetails = (props: Props) => {
	const classes = useStyles();
	const { cancellationData, authData } = props;
	const {
		reinstatementUpdateObj,
		setReinstatementUpdateObj,
		selectedCancellation,
		policy,
	} = cancellationData;
	const { effectiveTimestamp: cancellationEffectiveTimestamp, reinstatement } =
		selectedCancellation;

	// Gets  policyEndTimestamps that are after the cancellations effectiveTimestamp
	let maxReinstatementTimestamps = policy.modifications
		.map((mod) => dayjs(parseInt(mod.policyEndTimestamp, 10)))
		.filter(
			(modificationDate) =>
				!!modificationDate &&
				modificationDate.isAfter(
					dayjs(parseInt(cancellationEffectiveTimestamp, 10)),
				),
		);

	// Find the latest of all dates after the cancellation effectiveTimestamp
	let maxReinstatementDayJs =
		maxReinstatementTimestamps.length > 0 &&
		maxReinstatementTimestamps.reduce((a, b) => (a.isAfter(b) ? a : b));

	// as found via QE-2843, not all policy.create modifications contain a policyEndTimestamp which may cause maxReinstatementTimestamp to be undefined
	// if no modification has a policyEndTimestamp after the cancellation, use the originalContractEndDate as the maxEffectiveDate
	if (!maxReinstatementDayJs) {
		maxReinstatementDayJs = dayjs(
			parseInt(policy.originalContractEndTimestamp, 10),
		);
	}
	const minEffectiveDate = new Date(
		parseInt(cancellationEffectiveTimestamp, 10),
	);
	const maxEffectiveDate = maxReinstatementDayJs.toDate();

	maxEffectiveDate.setDate(maxEffectiveDate.getDate() - 1);

	const minDate = dayjs().add(1, 'day').startOf('day').toDate();

	const effectiveTimestamp =
		reinstatementUpdateObj.effectiveTimestamp ||
		reinstatement.effectiveTimestamp;
	const effectiveDateValue = effectiveTimestamp
		? new Date(parseInt(effectiveTimestamp, 10))
		: null;
	const [reinstatmentEffectiveDate, setReinstatmentEffectiveDate] =
		useState(effectiveDateValue);
	const effectiveDateErrors = getDateErrors(
		reinstatmentEffectiveDate,
		minEffectiveDate,
		maxEffectiveDate,
		true,
		authData,
	);

	const deadlineTimestamp =
		reinstatement.reinstatementDeadlineTimestamp ||
		reinstatementUpdateObj.reinstatementDeadlineTimestamp;
	const deadlineValue = deadlineTimestamp
		? new Date(parseInt(deadlineTimestamp, 10))
		: null;
	const [deadlineDate, setDeadlineDate] = useState(deadlineValue);
	const deadlineErrors = getReinstatementErrors(
		reinstatement,
		deadlineDate,
		minDate,
		false,
		authData,
	);

	useEffect(() => {
		setReinstatmentEffectiveDate(effectiveDateValue);
		setDeadlineDate(deadlineValue);
	}, [reinstatement, reinstatementUpdateObj]);

	const setNewEffectiveTimestamp = (date: Date) => {
		const validDate = date && !isNaN(date.getTime());
		setReinstatmentEffectiveDate(date);
		reinstatementUpdateObj.effectiveTimestamp = validDate
			? `${date.getTime()}`
			: null;
		setReinstatementUpdateObj(reinstatementUpdateObj);
	};

	const setNewReinstatementDeadlineTimestamp = (date: Date, value: string) => {
		const validDate = date && !isNaN(date.getTime());
		setDeadlineDate(date);
		reinstatementUpdateObj.reinstatementDeadlineTimestamp = validDate
			? `${date.getTime()}`
			: null;
		setReinstatementUpdateObj(reinstatementUpdateObj);
	};

	const renderReinstatementOverview = () => {
		const readOnlyMode =
			!authData.hasPermission('reinstatement.update') ||
			reinstatement.state !== 'draft';
		return (
			<StyledSectionContainer style={{ display: 'flex' }}>
				<StyledDetail style={{ width: '25%', flexGrow: 1 }}>
					<DefaultDatePicker
						id='effective-date'
						label='Effective Date'
						required={!readOnlyMode}
						style={{ marginRight: '10px' }}
						value={reinstatmentEffectiveDate}
						error={!readOnlyMode && !effectiveDateErrors.isValid}
						errorMessage={
							!readOnlyMode && !effectiveDateErrors.isValid
								? effectiveDateErrors.message
								: null
						}
						minDate={minEffectiveDate}
						maxDate={maxEffectiveDate}
						disabled={readOnlyMode}
						onChange={setNewEffectiveTimestamp}
						showCalendar
					/>
				</StyledDetail>
				<StyledDetail style={{ width: '25%', flexGrow: 1 }}>
					<DefaultDatePicker
						id='reinstatement-deadline-date'
						label='Reinstatement Deadline Date'
						// only required if reinstatementDeadlineTimestamp is initially set
						required={
							!!reinstatement.reinstatementDeadlineTimestamp && !readOnlyMode
						}
						style={{ marginRight: '10px' }}
						value={deadlineDate}
						error={!readOnlyMode && !deadlineErrors.isValid}
						errorMessage={
							!readOnlyMode && !deadlineErrors.isValid
								? deadlineErrors.message
								: null
						}
						onChange={setNewReinstatementDeadlineTimestamp}
						showCalendar
						disabled={readOnlyMode}
						minDate={minDate}
					/>
				</StyledDetail>
			</StyledSectionContainer>
		);
	};

	return (
		<>
			<div className={classes.linkWrapper}>
				{authData.hasPermission('auxData.fetch') && (
					<MaterialLink component={RouterLink} to='../auxData'>
						Aux Data
					</MaterialLink>
				)}
			</div>
			{renderReinstatementOverview()}
		</>
	);
};

export default withCancellationContext<Props>(ReinstatementDetails);
